import { AnyAction } from 'redux'
import { AuthStatusWrite } from 'src/queries/authStatusStore'
import {
  CSP_AUTHED_SUCCESS,
  DELETE_COGNITO_SIGNOUT_SUCCESS,
  DELETE_CSP_EMULATED_SESSIONS_FAILURE,
  DELETE_CSP_EMULATED_SESSIONS_SUCCESS,
  DELETE_SIGNOUT_SUCCESS,
  GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS,
  GET_CSP_ME_FAILURE,
  GET_CSP_ME_SUCCESS,
  GET_HEARTBEAT_FAILURE,
  GET_ME_FAILURE,
  GET_ME_SUCCESS,
  POPULATE_USER_INFO_LEGACY,
  POST_COGNITO_RENEW_AUTH_SUCCESS,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS,
  POST_CSP_LOGIN_SUCCESS,
  POST_RENEW_AUTH_SUCCESS,
  POST_SIGNIN_SUCCESS,
  PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS,
} from 'src/redux/actions/auth.actions'
import { DELETE_USER_SUCCESS } from 'src/redux/actions/data/users.actions'

/** Authoritative auth-status write logic, mirroring auth.reducer's isAuthenticated/isCspAuthenticated cases. See src/queries/AGENTS.md for the write-parity invariant. */

/** True when the action targets the CSP session (payload `isCsp`). */
const actionIsCsp = (action: AnyAction): boolean =>
  Boolean((action as { payload?: { isCsp?: boolean } }).payload?.isCsp)

export const resolveAuthStatusWrite = (
  action: AnyAction,
): AuthStatusWrite | undefined => {
  switch (action.type) {
    // --- primary session: isAuthenticated = true (unconditional in the reducer) ---
    case GET_ME_SUCCESS:
    case GET_COGNITO_SIGNIN_THEN_PUSH_SUCCESS:
    case POPULATE_USER_INFO_LEGACY:
    case POST_SIGNIN_SUCCESS:
    case POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS:
    case PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS:
    case PUT_CSP_EMULATED_SESSIONS_REFRESH_SUCCESS:
      return { isAuthenticated: true }

    // --- CSP session: isCspAuthenticated = true (unconditional in the reducer) ---
    // Mirrors auth.reducer GET_CSP_ME_SUCCESS / GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS
    // / POST_CSP_LOGIN_SUCCESS / CSP_AUTHED_SUCCESS: each sets only
    // `draft.isCspAuthenticated = true` and leaves `isAuthenticated` untouched.
    case GET_CSP_ME_SUCCESS:
    case GET_CSP_COGNITO_SIGNIN_THEN_PUSH_SUCCESS:
    case POST_CSP_LOGIN_SUCCESS:
    case CSP_AUTHED_SUCCESS:
      return { isCspAuthenticated: true }

    // --- primary session: isAuthenticated = false (unconditional in the reducer) ---
    // The CSP-emulated delete cases and DELETE_USER_SUCCESS clear only the primary flag
    // (they do NOT touch isCspAuthenticated in the reducer).
    case GET_ME_FAILURE:
    case DELETE_CSP_EMULATED_SESSIONS_SUCCESS:
    case DELETE_CSP_EMULATED_SESSIONS_FAILURE:
    case DELETE_USER_SUCCESS:
      return { isAuthenticated: false }

    // --- CSP session: isCspAuthenticated = false (unconditional in the reducer) ---
    // Mirrors auth.reducer GET_CSP_ME_FAILURE: clears only `draft.isCspAuthenticated`.
    case GET_CSP_ME_FAILURE:
      return { isCspAuthenticated: false }

    // Signout. Mirrors auth.reducer DELETE_(COGNITO_)SIGNOUT_SUCCESS: `isAuthenticated`
    // is cleared UNCONDITIONALLY; the `if (action.payload.isCsp)` block additionally
    // clears `isCspAuthenticated`. One write carrying both flags when `isCsp`.
    case DELETE_SIGNOUT_SUCCESS:
    case DELETE_COGNITO_SIGNOUT_SUCCESS:
      return actionIsCsp(action)
        ? { isAuthenticated: false, isCspAuthenticated: false }
        : { isAuthenticated: false }

    // Renew / refresh. Mirrors auth.reducer POST_(COGNITO_)RENEW_AUTH_SUCCESS: the
    // reducer sets `draft.isAuthenticated = true` on the non-CSP branch and
    // `draft.isCspAuthenticated = true` on the CSP branch — mutually exclusive, never
    // both, and it leaves the other flag untouched.
    case POST_RENEW_AUTH_SUCCESS:
    case POST_COGNITO_RENEW_AUTH_SUCCESS:
      return actionIsCsp(action)
        ? { isCspAuthenticated: true }
        : { isAuthenticated: true }

    // Heartbeat failure. Mirrors auth.reducer GET_HEARTBEAT_FAILURE: the reducer clears
    // `draft.isAuthenticated` on the non-CSP branch and `draft.isCspAuthenticated` on
    // the CSP branch — mutually exclusive, leaving the other flag untouched.
    case GET_HEARTBEAT_FAILURE:
      return actionIsCsp(action)
        ? { isCspAuthenticated: false }
        : { isAuthenticated: false }

    default:
      return undefined
  }
}
